import { useEffect, useRef, useState, type RefObject } from 'react'

export function usePlayerFullscreen(root: RefObject<HTMLDivElement | null>) {
  const [expanded, setExpanded] = useState(false)
  const [viewportFullscreen, setViewportFullscreen] = useState(false)
  const viewportFullscreenRef = useRef(false)
  viewportFullscreenRef.current = viewportFullscreen

  useEffect(() => {
    const update = () => setExpanded(document.fullscreenElement === root.current)
    document.addEventListener('fullscreenchange', update)
    return () => document.removeEventListener('fullscreenchange', update)
  }, [root])

  useEffect(() => {
    if (!viewportFullscreen) return
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setViewportFullscreen(false)
    }
    window.addEventListener('keydown', escape)
    return () => {
      document.body.style.overflow = overflow
      window.removeEventListener('keydown', escape)
    }
  }, [viewportFullscreen])

  async function toggle() {
    if (viewportFullscreenRef.current) {
      setViewportFullscreen(false)
      return
    }
    const element = root.current
    if (!element) return
    if (document.fullscreenElement === element) {
      await document.exitFullscreen()
      return
    }
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen()
        return
      }
    } catch {
      // iPhone Safari and restricted embeds may reject element fullscreen.
    }
    setViewportFullscreen(true)
  }

  return { expanded, viewportFullscreen, toggle }
}
